/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * What the homepage shows while the providers load: placeholders in the
 * silhouette of the comparer (ComparisonEngine.tsx), so the real list replaces
 * them in place rather than popping into an empty viewport. Until 2026-10-07
 * this file also chose between the rows and the old cards by a build-time
 * flag; the cards and the flag are gone (plan-vergelijker-resultaten §13.5).
 */

import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * The placeholders' classes, in the rows' silhouette (ResultRow.tsx): a panel
 * card on a phone (the logo, the name and the round profile arrow; one inset
 * panel with the net figure beside the CTA over the labelled facts; the
 * Vergelijk · Details strip), the two-line tablet card from sm (the phone's
 * facts panel up to lg, one line of five from lg), one compact row from xl.
 * Every row of a list is the same height since 2026-10-01: 290 / 197 / 144 /
 * 62 px (phone, sm to lg, lg to xl, xl) when no provider on screen has a
 * review score — since the terms became two values with the payout's timing
 * line under the frequency (plan-voorwaarden-vergelijking-2026-10-07 §6.1,
 * measured in the Phase 0 mockup and on the row, 2026-10-07; 262 / 169 / 132
 * / 62 before, with the line of five from md). The review slot makes the name
 * block 56 px instead of 40, and the rows 16 px taller where that block is the
 * tallest thing in its line (the phone, xl: 78 px there), 12 from sm to xl,
 * where the 44 px CTA already stands above the 40 px block — the skeleton
 * takes the first.
 */
const SKELETON_CLASSES = {
  list: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 space-y-3',
  item: 'h-[18.125rem] sm:h-[12.3125rem] lg:h-[9rem] xl:h-[3.875rem] rounded-xl xl:rounded-2xl bg-slate-800 border border-slate-700/60 animate-pulse',
};

export const ComparisonSkeleton: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className={SKELETON_CLASSES.list} role="status" aria-label={t('app.loading_providers')}>
      <div className="h-9 w-64 max-w-full rounded-xl bg-slate-800 animate-pulse mb-6" />
      {[0, 1, 2].map((i) => (
        <div key={i} className={SKELETON_CLASSES.item} style={{ animationDelay: `${i * 150}ms` }} />
      ))}
      <span className="sr-only">{t('app.loading_providers')}</span>
    </div>
  );
};
